import { Injectable, inject, signal } from '@angular/core';

import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';

import { CoverageOption } from '../models/package-configurator/coverage-option.model';
import { PackageTemplate, PackageTemplateKind } from '../models/package-configurator/package-template.model';
import { PackageTemplateService } from '../services/package-template.service';

/**
 * Root cache for the two template lists (content templates, option templates). Each kind is fetched from
 * the backend once and reused across the configurator page: `loadCoverageTemplates()` /
 * `loadOptionTemplates()` return the cache when present, otherwise make a single shared request.
 * Templates created meanwhile are added with `addCoverageTemplate()` / `addOptionTemplate()` and survive a
 * list load that was already in flight. An option template's full body is cached per id by
 * `loadOptionTemplate()` — templates are snapshots, so a fetched body never changes.
 * `invalidate()` drops both lists so the next load re-fetches them.
 */
@Injectable({ providedIn: 'root' })
export class PackageTemplateStore {
  private readonly service = inject(PackageTemplateService);

  private readonly coverageTemplatesSignal = signal<PackageTemplate[]>([]);
  private coverageLoaded = false;
  private coverageRequest$?: Observable<PackageTemplate[]>;

  private readonly optionTemplatesSignal = signal<PackageTemplate[]>([]);
  private optionLoaded = false;
  private optionRequest$?: Observable<PackageTemplate[]>;

  private readonly optionBodies = new Map<string, CoverageOption>();
  private readonly optionBodyRequests = new Map<string, Observable<CoverageOption>>();

  readonly coverageTemplates = this.coverageTemplatesSignal.asReadonly();
  readonly optionTemplates = this.optionTemplatesSignal.asReadonly();

  loadCoverageTemplates(): Observable<PackageTemplate[]> {
    if (this.coverageLoaded) return of(this.coverageTemplatesSignal());

    this.coverageRequest$ ??= this.service.getTemplates(PackageTemplateKind.Coverage).pipe(
      tap(templates => {
        this.coverageTemplatesSignal.update(created => withCreated(templates, created));
        this.coverageLoaded = true;
      }),
      map(() => this.coverageTemplatesSignal()),
      catchError(error => {
        this.coverageRequest$ = undefined;
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.coverageRequest$;
  }

  loadOptionTemplates(): Observable<PackageTemplate[]> {
    if (this.optionLoaded) return of(this.optionTemplatesSignal());

    this.optionRequest$ ??= this.service.getTemplates(PackageTemplateKind.Option).pipe(
      tap(templates => {
        this.optionTemplatesSignal.update(created => withCreated(templates, created));
        this.optionLoaded = true;
      }),
      map(() => this.optionTemplatesSignal()),
      catchError(error => {
        this.optionRequest$ = undefined;
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.optionRequest$;
  }

  /**
   * Adds a just-created option template to the cache so it can be applied without a re-fetch. Does not mark
   * the list as loaded — the rest of it still has to come from the backend.
   */
  addOptionTemplate(template: PackageTemplate): void {
    this.optionTemplatesSignal.update(list => withCreated(list, [template]));
  }

  /** Adds a just-created coverage (content) template to the cache; the rest of the list still loads from the backend. */
  addCoverageTemplate(template: PackageTemplate): void {
    this.coverageTemplatesSignal.update(list => withCreated(list, [template]));
  }

  /** An option template's full body (limits, clinic classes, sub-options), fetched once per template. Returns a copy. */
  loadOptionTemplate(templateId: string): Observable<CoverageOption> {
    const cached = this.optionBodies.get(templateId);
    if (cached) return of(structuredClone(cached));

    let request$ = this.optionBodyRequests.get(templateId);
    if (!request$) {
      request$ = this.service.getOptionTemplate(templateId).pipe(
        tap(option => this.optionBodies.set(templateId, structuredClone(option))),
        finalize(() => this.optionBodyRequests.delete(templateId)),
        shareReplay(1)
      );
      this.optionBodyRequests.set(templateId, request$);
    }

    return request$.pipe(map(option => structuredClone(option)));
  }

  /** Drops both caches so the next load re-fetches — call after a template is created or deleted. */
  invalidate(): void {
    this.coverageLoaded = false;
    this.coverageRequest$ = undefined;
    this.optionLoaded = false;
    this.optionRequest$ = undefined;
  }
}

/** The fetched list plus any template created meanwhile that it does not contain yet. */
function withCreated(templates: PackageTemplate[], created: PackageTemplate[]): PackageTemplate[] {
  const known = new Set(templates.map(template => template.id));
  return [...templates, ...created.filter(template => !known.has(template.id))];
}
